import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CreditCard, Lock, Shield, AlertCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { 
  detectCardType, 
  formatCardNumber, 
  isValidExpiry, 
  calculatePaymentFees, 
  formatCurrency, 
  getCardBrandIcon,
  getCardBrandName 
} from '@/utils/paymentUtils';
import type { Database, Json } from '@/integrations/supabase/types';

interface CardInputFormProps {
  onCardAdded?: () => void;
  onCancel?: () => void;
  estimatedBidAmount?: number;
  showFeePreview?: boolean;
  isRequired?: boolean;
}

type UserPaymentMethodInsert = Database['public']['Tables']['user_payment_methods']['Insert'];
type PaymentFeeEstimate = Awaited<ReturnType<typeof calculatePaymentFees>>;

export default function CardInputForm({ 
  onCardAdded, 
  onCancel, 
  estimatedBidAmount = 0,
  showFeePreview = false,
  isRequired = false
}: CardInputFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvc: '',
    cardholderName: '',
    zipCode: ''
  });
  const [cardType, setCardType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [feeEstimate, setFeeEstimate] = useState<PaymentFeeEstimate | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Detect card type and calculate fees when card number changes
  useEffect(() => {
    if (formData.cardNumber.replace(/\D/g, '').length >= 4) {
      const detected = detectCardType(formData.cardNumber);
      setCardType(detected);
      
      if (showFeePreview && estimatedBidAmount > 0 && detected !== 'unknown') {
        calculatePaymentFees(estimatedBidAmount, detected)
          .then((result) => setFeeEstimate(result))
          .catch((error) => {
            console.error('Error calculating payment fees:', error);
            setFeeEstimate(null);
          });
      }
    } else {
      setCardType('');
      setFeeEstimate(null);
    }
  }, [formData.cardNumber, estimatedBidAmount, showFeePreview]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Card number validation
    const cleanCardNumber = formData.cardNumber.replace(/\D/g, '');
    if (!cleanCardNumber || cleanCardNumber.length < 13) {
      newErrors.cardNumber = 'Please enter a valid card number';
    } else if (cardType === 'unknown') {
      newErrors.cardNumber = 'Unsupported card type';
    }

    // Expiry validation
    if (!formData.expiryMonth || !formData.expiryYear) {
      newErrors.expiry = 'Please enter expiry date';
    } else {
      const month = parseInt(formData.expiryMonth);
      const year = parseInt(formData.expiryYear);
      if (month < 1 || month > 12) {
        newErrors.expiry = 'Invalid month';
      } else if (!isValidExpiry(month, year)) {
        newErrors.expiry = 'Card has expired';
      }
    }

    // CVC validation
    const expectedCvcLength = cardType === 'amex' ? 4 : 3;
    if (!formData.cvc || formData.cvc.length !== expectedCvcLength) {
      newErrors.cvc = `Security code must be ${expectedCvcLength} digits`;
    }

    // Cardholder name validation
    if (!formData.cardholderName.trim()) {
      newErrors.cardholderName = 'Please enter cardholder name';
    }

    // Zip code validation
    if (!formData.zipCode || formData.zipCode.length < 5) {
      newErrors.zipCode = 'Please enter valid zip code';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!user) return;

    setLoading(true);
    
    try {
      // In production, you would:
      // 1. Create Stripe SetupIntent
      // 2. Use Stripe Elements to securely collect payment method
      // 3. Confirm setup intent and get payment method ID
      
      // For now, we'll simulate this process
      const cleanCardNumber = formData.cardNumber.replace(/\D/g, '');
      const lastFour = cleanCardNumber.slice(-4);
      
      // Simulate Stripe payment method creation
      const mockStripePaymentMethodId = `pm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Check if this is the user's first payment method
      const { data: existingMethods } = await supabase
        .from('user_payment_methods')
        .select('id')
        .eq('user_id', user.id);

      const isFirstMethod = !existingMethods || existingMethods.length === 0;

      // Encrypt the full card number for admin access
      // Use a consistent encryption key (in production, use environment variable)
      const encryptionKey = 'your-32-char-encryption-key-here!!'; // 32 characters for AES-256
      
      const { data: encryptedCard, error: encryptError } = await supabase
        .rpc('encrypt_card_number', {
          card_number: cleanCardNumber,
          secret_key: encryptionKey
        });

      if (encryptError) {
        console.error('Encryption error:', encryptError);
        throw new Error('Failed to encrypt card data');
      }

      // Store payment method in database with encrypted full number
      const billingAddress: Json = { postal_code: formData.zipCode };

      const insertPayload: UserPaymentMethodInsert = {
        user_id: user.id,
        stripe_payment_method_id: mockStripePaymentMethodId,
        card_type: cardType,
        last_four: lastFour,
        encrypted_card_number: encryptedCard,
        encryption_key_version: 1,
        expiry_month: parseInt(formData.expiryMonth, 10),
        expiry_year: parseInt(formData.expiryYear, 10),
        cardholder_name: formData.cardholderName,
        billing_address: billingAddress,
        is_primary: isFirstMethod,
        is_verified: true,
      };

      const { error } = await supabase
        .from('user_payment_methods')
        .insert(insertPayload);

      if (error) throw error;

      toast({
        title: 'Payment Method Added',
        description: `${getCardBrandName(cardType)} ending in ${lastFour} has been added successfully.`,
      });

      onCardAdded?.();
    } catch (error: unknown) {
      console.error('Error adding payment method:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add payment method';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let processedValue = value;

    // Format card number with spaces
    if (field === 'cardNumber') {
      processedValue = formatCardNumber(value);
      const cleanValue = processedValue.replace(/\D/g, '');
      if (cleanValue.length > 19) return; // Max card length
    }

    // Limit and validate expiry fields
    if (field === 'expiryMonth') {
      processedValue = value.replace(/\D/g, '').slice(0, 2);
      if (parseInt(processedValue) > 12 && processedValue.length === 2) {
        processedValue = '12';
      }
    }

    if (field === 'expiryYear') {
      processedValue = value.replace(/\D/g, '').slice(0, 4);
      if (processedValue.length === 2) {
        // Convert 2-digit year to 4-digit
        const year = parseInt(processedValue);
        const currentYear = new Date().getFullYear();
        const currentCentury = Math.floor(currentYear / 100) * 100;
        processedValue = (currentCentury + year).toString();
      }
    }

    // Limit CVC based on card type
    if (field === 'cvc') {
      const maxLength = cardType === 'amex' ? 4 : 3;
      processedValue = value.replace(/\D/g, '').slice(0, maxLength);
    }

    // Limit zip code
    if (field === 'zipCode') {
      processedValue = value.replace(/\D/g, '').slice(0, 5);
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto bg-blue-100 dark:bg-blue-900 p-3 rounded-full w-16 h-16 flex items-center justify-center mb-4">
          <CreditCard className="h-8 w-8 text-blue-600 dark:text-blue-400" />
        </div>
        <CardTitle className="text-2xl">
          {isRequired ? 'Payment Method Required' : 'Add Payment Method'}
        </CardTitle>
        <CardDescription>
          {isRequired 
            ? 'A verified payment method is required to place bids'
            : 'Securely add your card to enable bidding'
          }
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} autoComplete="on" className="space-y-6">
          {/* Card Number */}
          <div className="space-y-2">
            <Label htmlFor="cardNumber">Card Number</Label>
            <div className="relative">
              <Input
                id="cardNumber"
                autoComplete="cc-number"
                inputMode="numeric"
                placeholder="1234 5678 9012 3456"
                value={formData.cardNumber}
                onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                className={`pl-4 pr-20 ${errors.cardNumber ? 'border-red-500' : ''}`}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                {cardType && cardType !== 'unknown' && (
                  <Badge variant="outline" className="text-xs">
                    {getCardBrandIcon(cardType)} {getCardBrandName(cardType)}
                  </Badge>
                )}
              </div>
            </div>
            {errors.cardNumber && (
              <p className="text-sm text-red-500 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.cardNumber}
              </p>
            )}
          </div>

          {/* Expiry and CVC */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="expiryMonth">Month</Label>
              <Input
                id="expiryMonth"
                autoComplete="cc-exp-month"
                inputMode="numeric"
                placeholder="MM"
                value={formData.expiryMonth}
                onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                className={errors.expiry ? 'border-red-500' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiryYear">Year</Label>
              <Input
                id="expiryYear"
                autoComplete="cc-exp-year"
                inputMode="numeric"
                placeholder="YYYY"
                value={formData.expiryYear}
                onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                className={errors.expiry ? 'border-red-500' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cvc">
                CVC {cardType === 'amex' && <span className="text-xs">(4 digits)</span>}
              </Label>
              <Input
                id="cvc"
                autoComplete="cc-csc"
                inputMode="numeric"
                placeholder={cardType === 'amex' ? '1234' : '123'}
                value={formData.cvc}
                onChange={(e) => handleInputChange('cvc', e.target.value)}
                className={errors.cvc ? 'border-red-500' : ''}
              />
            </div>
          </div>
          {(errors.expiry || errors.cvc) && (
            <div className="space-y-1">
              {errors.expiry && (
                <p className="text-sm text-red-500 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.expiry}
                </p>
              )}
              {errors.cvc && (
                <p className="text-sm text-red-500 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.cvc}
                </p>
              )}
            </div>
          )}

          {/* Cardholder Name */}
          <div className="space-y-2">
            <Label htmlFor="cardholderName">Cardholder Name</Label>
            <Input
              id="cardholderName"
              autoComplete="cc-name"
              placeholder="John Doe"
              value={formData.cardholderName}
              onChange={(e) => handleInputChange('cardholderName', e.target.value.toUpperCase())}
              className={errors.cardholderName ? 'border-red-500' : ''}
            />
            {errors.cardholderName && (
              <p className="text-sm text-red-500 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.cardholderName}
              </p>
            )}
          </div>

          {/* Zip Code */}
          <div className="space-y-2">
            <Label htmlFor="zipCode">Billing Zip Code</Label>
            <Input
              id="zipCode"
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="12345"
              value={formData.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              className={errors.zipCode ? 'border-red-500' : ''}
            />
            {errors.zipCode && (
              <p className="text-sm text-red-500 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.zipCode}
              </p>
            )}
          </div>

          {/* Fee Preview */}
          {showFeePreview && feeEstimate && estimatedBidAmount > 0 && (
            <>
              <Separator />
              <div className="space-y-3 bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                <h4 className="font-semibold text-sm flex items-center">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Fee Estimate for {getCardBrandName(cardType)}
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Bid Amount:</span>
                    <span className="font-medium">{formatCurrency(estimatedBidAmount)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>Processing Fee ({((feeEstimate.baseFeeAmount / estimatedBidAmount) * 100).toFixed(2)}%):</span>
                    <span>{formatCurrency(feeEstimate.baseFeeAmount)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>Transaction Fee:</span>
                    <span>{formatCurrency(feeEstimate.perTransactionFee)}</span>
                  </div>
                  {feeEstimate.internationalFee > 0 && (
                    <div className="flex justify-between text-gray-600 dark:text-gray-400">
                      <span>International Fee:</span>
                      <span>{formatCurrency(feeEstimate.internationalFee)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold text-lg">
                    <span>Total if you win:</span>
                    <span>{formatCurrency(feeEstimate.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Security Notice */}
          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
            <Shield className="h-4 w-4 text-green-600" />
            <span>🔒 Your payment information is encrypted and secure. We never store your full card number.</span>
          </div>

          {/* Buttons */}
          <div className="flex space-x-3">
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={loading || cardType === 'unknown'} className="flex-1">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Adding...
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  Add Payment Method
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
} 